import { type Transaction, TRANSACTION_FILTER_KINDS } from '@familywallet/shared';
import { CalendarClock, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { FormError } from '../components/FormError';
import { Chip, Row } from '../components/FormRow';
import { PageHeader } from '../components/PageHeader';
import { PickerSheet, type PickerOption } from '../components/PickerSheet';
import { TransactionRow } from '../components/TransactionRow';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, ListGroup, SectionLabel } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useMe } from '../lib/auth';
import { formatDay, todayIn } from '../lib/dates';
import {
  type TransactionFilters,
  useAccounts,
  useCategories,
  usePayeeList,
  useTransactions,
} from '../lib/ledger';
import { useMembers } from '../lib/members';
import { cn } from '../lib/utils';

/** Transactions grouped by day, newest first, as the list shows them. */
export function groupByDay(items: readonly Transaction[]): { day: string; items: Transaction[] }[] {
  const groups: { day: string; items: Transaction[] }[] = [];
  for (const tx of items) {
    const last = groups.at(-1);
    if (last && last.day === tx.occurredOn) last.items.push(tx);
    else groups.push({ day: tx.occurredOn, items: [tx] });
  }
  return groups;
}

type FilterKey = 'kind' | 'accountId' | 'categoryId' | 'payeeId' | 'memberId' | 'from' | 'to';
const FILTER_KEYS: readonly FilterKey[] = [
  'kind',
  'accountId',
  'categoryId',
  'payeeId',
  'memberId',
  'from',
  'to',
];

/** The filters in the address, so a filtered list can be linked to (from a payee, an account). */
function useFilters() {
  const [params, setParams] = useSearchParams();
  const filters: TransactionFilters = {};
  for (const key of FILTER_KEYS) {
    const value = params.get(key);
    if (value) (filters as Record<string, string>)[key] = value;
  }
  if (filters.kind && !(TRANSACTION_FILTER_KINDS as readonly string[]).includes(filters.kind)) {
    delete filters.kind;
  }
  const q = params.get('q') ?? '';
  const set = (changes: Partial<Record<FilterKey | 'q', string | null>>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(changes)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  return { filters, q, set, clear: () => setParams({}, { replace: true }) };
}

/**
 * Everything the viewer may see, newest first, grouped by day: search words, a kind, and more
 * filters (account, category, payee, member, dates).
 */
export function ActivityPage() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const { filters, q, set, clear } = useFilters();
  const [search, setSearch] = useState(q);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.trim() !== q) set({ q: search.trim() || null });
    }, 300);
    return () => clearTimeout(timer);
    // Only the typed words start a search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  const query = { ...filters, ...(q ? { q } : {}) };
  const list = useTransactions(30, query);
  const more = FILTER_KEYS.filter((k) => k !== 'kind' && filters[k]).length;
  const filtered = Object.keys(query).length > 0;
  if (!me.data) return <p role="status">{t('app.loading')}</p>;
  const { baseCurrency, timezone } = me.data.family;
  const today = todayIn(timezone);
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('activity.title')}
        action={
          <Link to="/recurring" className={buttonVariants({ variant: 'secondary' })}>
            <CalendarClock aria-hidden="true" className="size-5" strokeWidth={2} />
            {t('recurring.entry')}
          </Link>
        }
      />
      <Input
        type="search"
        aria-label={t('filters.search')}
        placeholder={t('filters.search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div
        className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1"
        role="group"
        aria-label={t('filters.kind')}
      >
        {[undefined, ...TRANSACTION_FILTER_KINDS].map((k) => (
          <button
            key={k ?? 'all'}
            type="button"
            aria-pressed={filters.kind === k}
            onClick={() => set({ kind: k ?? null })}
            className={cn(
              'min-h-11 shrink-0 rounded-full px-4 text-sm font-medium focus-visible:outline-2 focus-visible:outline-accent',
              filters.kind === k ? 'bg-fg text-bg' : 'bg-surface-2 text-fg hover:bg-line',
            )}
          >
            {t(`filters.kinds.${k ?? 'all'}`)}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <SlidersHorizontal aria-hidden="true" className="size-5" strokeWidth={2} />
          {more > 0 ? t('filters.moreCount', { count: more }) : t('filters.more')}
        </Button>
        {filtered && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearch('');
              clear();
            }}
          >
            {t('filters.clear')}
          </Button>
        )}
      </div>
      {open && <FilterPanel filters={filters} set={set} today={today} />}

      {list.isPending ? (
        <p role="status">{t('app.loading')}</p>
      ) : list.isError ? (
        <Card role="alert" className="flex flex-col gap-3">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()}>
            {t('common.retry')}
          </Button>
        </Card>
      ) : items.length === 0 ? (
        <Card className="flex flex-col gap-4">
          <p className="text-muted">{filtered ? t('filters.none') : t('activity.empty')}</p>
          {!filtered && (
            <Link to="/add" className={buttonVariants()}>
              {t('add.title')}
            </Link>
          )}
        </Card>
      ) : (
        groupByDay(items).map((group) => (
          <section key={group.day} className="flex flex-col gap-2">
            <SectionLabel>
              {formatDay(group.day, today, i18n.language, {
                today: t('activity.today'),
                yesterday: t('activity.yesterday'),
              })}
            </SectionLabel>
            <ListGroup>
              {group.items.map((tx) => (
                <li key={tx.id}>
                  <TransactionRow tx={tx} baseCurrency={baseCurrency} />
                </li>
              ))}
            </ListGroup>
          </section>
        ))
      )}
      {list.hasNextPage && (
        <Button
          variant="secondary"
          disabled={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        >
          {list.isFetchingNextPage ? t('app.loading') : t('activity.loadMore')}
        </Button>
      )}
    </div>
  );
}

/** Account, category, payee, member and dates, each picked from what the viewer can see. */
function FilterPanel({
  filters,
  set,
  today,
}: {
  filters: TransactionFilters;
  set: (changes: Partial<Record<FilterKey, string | null>>) => void;
  today: string;
}) {
  const { t } = useTranslation();
  const [picker, setPicker] = useState<'accountId' | 'categoryId' | 'payeeId' | 'memberId' | null>(
    null,
  );
  const [pickerSearch, setPickerSearch] = useState('');
  const accounts = useAccounts(true);
  const expense = useCategories('expense', true);
  const income = useCategories('income', true);
  const payees = usePayeeList(picker === 'payeeId' ? pickerSearch.trim() : '');
  const members = useMembers();
  const options: Record<NonNullable<typeof picker>, PickerOption[]> = {
    accountId: (accounts.data?.items ?? []).map((a) => ({ value: a.id, label: a.name })),
    categoryId: [...(expense.data?.items ?? []), ...(income.data?.items ?? [])].map((c) => ({
      value: c.id,
      label: c.name,
      hint: t(`filters.kinds.${c.kind}`),
    })),
    payeeId: (payees.data?.items ?? []).map((p) => ({ value: p.id, label: p.name })),
    memberId: (members.data?.pages.flatMap((p) => p.items) ?? []).map((m) => ({
      value: m.id,
      label: m.displayName,
    })),
  };
  const label = (key: NonNullable<typeof picker>) => {
    const id = filters[key];
    if (!id) return t('filters.any');
    return options[key].find((o) => o.value === id)?.label ?? t('filters.chosen');
  };
  const needle = pickerSearch.trim().toLowerCase();
  return (
    <>
      <ListGroup>
        {(['accountId', 'categoryId', 'payeeId', 'memberId'] as const).map((key) => (
          <Row key={key} label={t(`filters.${key}`)}>
            <span className="flex items-center gap-1">
              <Chip
                onClick={() => {
                  setPickerSearch('');
                  setPicker(key);
                }}
              >
                {label(key)}
              </Chip>
              {filters[key] && (
                <button
                  type="button"
                  aria-label={t('filters.remove', { name: t(`filters.${key}`) })}
                  onClick={() => set({ [key]: null })}
                  className="flex size-11 items-center justify-center rounded-full text-muted hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
                >
                  <X aria-hidden="true" className="size-4" strokeWidth={2} />
                </button>
              )}
            </span>
          </Row>
        ))}
        {(['from', 'to'] as const).map((key) => (
          <Row key={key} label={t(`filters.${key}`)} htmlFor={`filter-${key}`}>
            <input
              id={`filter-${key}`}
              type="date"
              max={today}
              value={filters[key] ?? ''}
              onChange={(e) => set({ [key]: e.target.value || null })}
              className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
            />
          </Row>
        ))}
      </ListGroup>
      {picker && (
        <PickerSheet
          title={t(`filters.${picker}`)}
          search={{ value: pickerSearch, onChange: setPickerSearch, label: t('filters.search') }}
          options={
            picker === 'payeeId'
              ? options.payeeId
              : options[picker].filter((o) => o.label.toLowerCase().includes(needle))
          }
          selected={filters[picker] ?? ''}
          onPick={(id) => {
            set({ [picker]: id });
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
          empty={t('filters.noOptions')}
        />
      )}
    </>
  );
}
